import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import AppRoutes from "../../../../routes/AppRoutes";
import { Tooltip } from "../../../common";
import ManagerDashboardUpdateDto from "../../../../types/dtos/dashboards/ManagerDashboardUpdateDto";
import { ManagerTeamTodoUserDto } from "../../../../types/dtos/dashboards/right-bar/ManagerTeamTodosSectionDto";
import {
  getManagerTodoDescriptorBadgeText,
  managerUpdateUsesCollabDocLink,
} from "../../../manager-dashboard/shared/managerTodoHelper";

interface ManagerTeamTodoSidebarItemProps {
  update: ManagerDashboardUpdateDto;
  users: ManagerTeamTodoUserDto[];
  onDualPrepClick: (employeeId: number, journeyRef: string) => void;
  onSkipPlanningClick: (update: ManagerDashboardUpdateDto) => void;
}

function ManagerTeamTodoSidebarItem({
  update,
  users,
  onDualPrepClick,
  onSkipPlanningClick,
}: ManagerTeamTodoSidebarItemProps) {
  const { t } = useTranslation();
  const displayName =
    users.find((u) => u.userId === update.userId)?.fullName ?? "";
  const updateTimestamp = dayjs(update.timestamp);
  const relativeTime = updateTimestamp
    .startOf("day")
    .from(dayjs().startOf("day"));
  const descriptorText = getManagerTodoDescriptorBadgeText(update, t);
  const journeyTitle = update.clientFormTitle
    ? t(update.clientFormTitle)
    : "";

  const reviewButton = (
    <span className="shrink-0 primary-bg text-white text-xs px-2 py-1 rounded-sm hover:opacity-90 whitespace-nowrap self-center">
      {t("DashboardRightBar.Review")}
      <FontAwesomeIcon icon={faArrowRight} size="xs" className="ml-1" />
    </span>
  );

  const rowContent = (
    <div className="flex flex-row items-start gap-2 p-3 min-w-0">
      <div className="flex-grow min-w-0">
        <div className="text-xs text-gray-500 truncate">{displayName}</div>
        {journeyTitle ? (
          <Tooltip
            content={journeyTitle}
            triggerElement={
              <div className="text-sm font-semibold text-primary truncate leading-snug">
                {journeyTitle}
              </div>
            }
          />
        ) : null}
        <div className="text-xs text-slate-400 mt-0.5 truncate">
          {descriptorText ? (
            <>
              <span>{descriptorText}</span>
              <span className="mx-1">·</span>
            </>
          ) : null}
          <span>{relativeTime}</span>
        </div>
      </div>
      {reviewButton}
    </div>
  );

  const itemClassName =
    "to-do-list-item bg-white rounded-md border border-gray-300 hover:drop-shadow-md block";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2 items-stretch min-w-0">
        <div className="flex-grow min-w-0">
          {managerUpdateUsesCollabDocLink(update) ? (
            <Link
              to={AppRoutes.collaborativeDocument.generateRoute(
                update.answerSetUniqueId!,
                false,
                null
              )}
              className={`${itemClassName} cursor-pointer`}
            >
              {rowContent}
            </Link>
          ) : (
            <div
              className={`${itemClassName} cursor-pointer`}
              role="button"
              tabIndex={0}
              onClick={() =>
                onDualPrepClick(update.userId, update.journeyReference!)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onDualPrepClick(update.userId, update.journeyReference!);
                }
              }}
            >
              {rowContent}
            </div>
          )}
        </div>
      </div>
      {update.updateType === "DUAL-PREP-PLANNING" && update.canBeSkipped && (
        <button
          type="button"
          className="btn-secondary bg-white self-start text-xs ml-1"
          onClick={() => onSkipPlanningClick(update)}
        >
          {t("DashboardRightBar.SkipPlanning", "Skip planning")}
        </button>
      )}
    </div>
  );
}

export default ManagerTeamTodoSidebarItem;
